// @flow

import * as React from 'react';

import { mapURLSearchParamsToQuery } from '@gandi/app-runner';
import type { ProductData } from '@gandi/cart';
import type { TranslatorProps } from '@gandi/translate';
import { Base } from '@gandi/ui';

import type { WithRouterProps } from 'src/helpers/withRouter';

import DomainPageTitle from '../../components/DomainPageTitle';
import { Layout } from '../../components/Layout';
import { handleMarketingParams } from '../../modules/app/actions';
import type { StaticHandlerArgsWithMarketing } from '../../modules/app/actions';
import setPublicHTTPCache from '../../modules/app/public-http-cache';

import type { PreFetchArgs, FullState } from '../../types';
import {
  setSearchValue,
  type FetchResultsQuotation,
  type FetchResultsQuotationReturn,
} from '../modules/actions';

import type { SearchState, ExtendedResult } from '../types';

import DomainInput from './DomainInput';
import { NDSSwitch } from './NDSSwitch';

export type Query = {
  search?: string,
};

export type PresenterStateProps = {
  search: SearchState,
  results: Array<ExtendedResult>,
};

export type PresenterDispatchProps = {
  fetchResultsQuotation: FetchResultsQuotation<FetchResultsQuotationReturn>,
  submitSearch: (value: string) => void,
  addRewableResults: (
    translator: TranslatorProps,
  ) => Promise<Array<ProductData>>,
};

export type PresenterInjectedProps = {
  ...WithRouterProps<void, Query>,
  translator: TranslatorProps,
};

export type PresenterProps = {
  ...PresenterStateProps,
  ...PresenterDispatchProps,
  ...PresenterInjectedProps,
};

const inputId = 'search';

export default class Presenter extends React.Component<PresenterProps> {
  static async handleRedirect(args: StaticHandlerArgsWithMarketing<>) {
    await handleMarketingParams(args);
    await Layout.handleRedirect(args);
    // $FlowFixMe[incompatible-call]
    setPublicHTTPCache(args);
  }

  static async preFetch(args: PreFetchArgs<{}, void, Query, FullState>) {
    await Layout.preFetch(args);

    const search = args.query.search;
    /**
     * Save the value of the 'search' query params if present in
     * the query string (to automatically start a search from an url)
     */
    if (search) {
      args.dispatch(setSearchValue(search));
    }
  }

  componentDidUpdate(prevProps: PresenterProps) {
    if (!this.props.search.loading && prevProps.search.loading) {
      this.props.fetchResultsQuotation();
    }
  }

  componentDidMount() {
    const { search, translator } = this.props;

    this.props.fetchResultsQuotation();

    const query: Query = mapURLSearchParamsToQuery(this.props.searchParams);

    // Client-side links to /domain/transfer
    if (query.search && !this.props.results.length) {
      this.props.submitSearch(`${query.search}`);
    }
    if (search.value) {
      // Auto-add existing search results to the cart.
      this.props.addRewableResults(translator);
    }
  }

  render(): React.Node {
    const { translator } = this.props;

    const title = <DomainPageTitle process="renew" />;

    return (
      <Layout>
        <Base marginBottom="base">
          <DomainInput
            id={inputId}
            process="renew"
            title={title}
            label={translator.__('Renew a domain name')}
          />
          <NDSSwitch />
        </Base>
      </Layout>
    );
  }
}
